import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatDate, formatMoney } from '@/utils/format';
import { balanceAfterAdvance } from '@/utils/invoice';
import type { Invoice } from '@/types/portal';

interface InvoiceHeaderProps {
  invoice: Invoice;
  status: string;
  canApprove: boolean;
  onReview: () => void;
  onHold: () => void;
}

const facts = (invoice: Invoice) => [
  { icon: 'ri-wallet-3-line', label: 'Wallet', value: invoice.walletStatus },
  { icon: 'ri-shield-check-line', label: 'KYC', value: invoice.kycStatus },
  { icon: 'ri-bank-card-line', label: 'Payout rail', value: invoice.rail },
  { icon: 'ri-movie-line', label: 'Deliverable', value: invoice.deliverableRef },
];

export default function InvoiceHeader({
  invoice,
  status,
  canApprove,
  onReview,
  onHold,
}: InvoiceHeaderProps) {
  const locked = status === 'Paid' || status === 'Rejected';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <Avatar src={invoice.avatar} alt={invoice.creatorName} size="lg" />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl font-semibold text-foreground-950">
                {invoice.number}
              </h1>
              <StatusBadge status={status} />
            </div>
            <p className="mt-1 text-sm text-foreground-700">
              {invoice.creatorName} · {invoice.market}
            </p>
            <p className="text-xs text-foreground-500">
              {invoice.campaign} · issued {formatDate(invoice.issuedAt)} · due{' '}
              {formatDate(invoice.dueAt)}
            </p>
            {invoice.flags.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {invoice.flags.map((flag) => (
                  <StatusBadge key={flag} status={flag} />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-3 lg:items-end">
          <div className="text-left lg:text-right">
            <p className="text-xs text-foreground-500">Invoice total</p>
            <p className="font-heading text-2xl font-semibold text-foreground-950">
              {formatMoney(invoice.amount, invoice.currency)}
            </p>
            {invoice.advance ? (
              <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-secondary-800">
                <i className="ri-hand-coin-line" />
                Balance payable{' '}
                {formatMoney(balanceAfterAdvance(invoice.amount, invoice.advance), invoice.currency)}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" icon="ri-file-download-line">
              PDF
            </Button>
            <Button variant="outline" size="sm" icon="ri-printer-line">
              Print
            </Button>
            {locked ? null : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  icon="ri-pause-circle-line"
                  onClick={onHold}
                  disabled={!canApprove || status === 'On hold'}
                >
                  Hold
                </Button>
                <Button size="sm" icon="ri-check-line" onClick={onReview} disabled={!canApprove}>
                  Review decision
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-px border-t border-background-200 bg-background-200 sm:grid-cols-2 lg:grid-cols-4">
        {facts(invoice).map((fact) => (
          <div key={fact.label} className="bg-background-50 px-5 py-3.5">
            <p className="flex items-center gap-1.5 text-[11px] font-label font-medium uppercase tracking-wider text-foreground-400">
              <i className={fact.icon} />
              {fact.label}
            </p>
            <p className="mt-1 text-sm text-foreground-800">{fact.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}